"use client";

import { useCallback, useState } from "react";
import { AutocompleteInput } from "@/components/search/autocomplete-input";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import type { PresetTypeSlug } from "@/features/presets";
import type { AutocompleteResult, VariantFilterValue } from "@/lib/api/types";
import { hasEntityForm } from "@/lib/search/entity-variants";
import { VariantsDropdown } from "../input/internal/variants-dropdown";
import {
  BulkModifyDropdown,
  ClearChipsButton,
  FilterChip,
  FilterFieldContainer,
  FilterFieldContent,
  FilterFieldLabel,
  FilterLabel,
  PresetButton,
} from "../shared";
import { FilterCheckboxInput } from "@/features/search/components/filters/input/checkbox-input";
import { FilterCompanyInput } from "@/features/search/components/filters/input/company-input";
import { FilterLocationInput } from "@/features/search/components/filters/input/location-input";
import { FilterNumberInput } from "@/features/search/components/filters/input/number-input";
import { parseLocationResult } from "@/features/search/lib/parse-location-result";
import { Icon } from "@/components/ui/icon";

type CompanySize =
  | "1-10"
  | "11-50"
  | "51-200"
  | "201-500"
  | "501-1000"
  | "1001-5000"
  | "5001-10000"
  | "10001+";

const COMPANY_SIZES: { value: CompanySize; label: string }[] = [
  { value: "1-10", label: "1–10" },
  { value: "11-50", label: "11–50" },
  { value: "51-200", label: "51–200" },
  { value: "201-500", label: "201–500" },
  { value: "501-1000", label: "501–1,000" },
  { value: "1001-5000", label: "1,001–5,000" },
  { value: "5001-10000", label: "5,001–10,000" },
  { value: "10001+", label: "10,001+" },
];

const COMPANY_SIZE_VALUES = new Set<string>(COMPANY_SIZES.map((s) => s.value));

const COMPANY_SIZE_LABELS: Record<string, string> = Object.fromEntries(
  COMPANY_SIZES.map((s) => [s.value, s.label]),
);

function isCompanySize(value: string): value is CompanySize {
  return COMPANY_SIZE_VALUES.has(value);
}

export function FilterCompanySection({
  onOpenPreset,
}: {
  onOpenPreset?: (type: PresetTypeSlug) => void;
}) {
  const { filters, updateFilter } = useTalentSearchFilters();
  const [companyExclusions, setCompanyExclusions] = useState(true);
  const companies = filters.companies ?? [];
  const tags = filters.company_tags ?? [];

  const addTag = (text: string) => {
    if (hasEntityForm(tags, text)) return;
    updateFilter("company_tags", [...tags, { text, required: false }]);
  };

  const updateCompany = useCallback(
    (index: number, updated: VariantFilterValue) => {
      const next = [...companies];
      next[index] = updated;
      updateFilter("companies", next);
    },
    [companies, updateFilter],
  );

  const removeCompany = useCallback(
    (index: number) => {
      updateFilter(
        "companies",
        companies.filter((_, j) => j !== index),
      );
    },
    [companies, updateFilter],
  );

  const bulkSetScope = useCallback(
    (scope: "c" | "p" | "cp") => {
      updateFilter(
        "companies",
        companies.map((c) => ({ ...c, scope })),
      );
    },
    [companies, updateFilter],
  );

  const bulkExclude = useCallback(() => {
    updateFilter(
      "companies",
      companies.map((c) => ({ ...c, mode: "exclude" as const })),
    );
  }, [companies, updateFilter]);

  const bulkUnexclude = useCallback(() => {
    updateFilter(
      "companies",
      companies.map((c) => ({ ...c, mode: "include" as const })),
    );
  }, [companies, updateFilter]);

  const bulkRequire = useCallback(() => {
    updateFilter(
      "companies",
      companies.map((c) => ({ ...c, required: true })),
    );
  }, [companies, updateFilter]);

  const bulkUnrequire = useCallback(() => {
    updateFilter(
      "companies",
      companies.map((c) => ({ ...c, required: false })),
    );
  }, [companies, updateFilter]);

  return (
    <>
      <div className="flex flex-col gap-1 w-full">
        <div className="flex items-center justify-between w-full">
          <FilterLabel>Companies</FilterLabel>
          {companies.length > 0 && (
            <BulkModifyDropdown
              onSetScope={bulkSetScope}
              onExcludeAll={bulkExclude}
              onUnexcludeAll={bulkUnexclude}
              onRequireAll={bulkRequire}
              onUnrequireAll={bulkUnrequire}
            />
          )}
        </div>
        <div className="flex flex-col">
          <AutocompleteInput
            variant="filter"
            field="companies"
            placeholder="Search..."
            allowFreeText={false}
            flatBottom
            onSelect={(result: AutocompleteResult) => {
              if (hasEntityForm(companies, result.value)) return;
              updateFilter("companies", [
                ...companies,
                {
                  canonical_id: result.canonical_id ?? undefined,
                  text: result.value,
                  domain: result.domain ?? undefined,
                  scope: "cp",
                  required: false,
                } as VariantFilterValue,
              ]);
            }}
          />
          <PresetButton
            label="Company Presets"
            onClick={() => onOpenPreset?.("companies")}
          />
        </div>
      </div>

      {companies.map((c, i) => (
        <FilterCompanyInput
          key={`${c.text}-${i}`}
          company={c}
          variantsField="companies"
          onChange={(updated) => updateCompany(i, updated)}
          onRemove={() => removeCompany(i)}
        />
      ))}

      {companies.length > 1 && (
        <div className="flex justify-end">
          <ClearChipsButton
            onClick={() => updateFilter("companies", undefined)}
          />
        </div>
      )}

      <FilterFieldContainer>
        <FilterFieldLabel>Company HQ Locations</FilterFieldLabel>
        <FilterFieldContent>
          <AutocompleteInput
            variant="filter"
            field="locations"
            placeholder="Search..."
            onSelect={(result: AutocompleteResult) => {
              const existing = filters.company_locations ?? [];
              updateFilter("company_locations", [
                ...existing,
                parseLocationResult(result),
              ]);
            }}
          />
        </FilterFieldContent>
      </FilterFieldContainer>
      {(filters.company_locations ?? []).map((loc, i) => (
        <FilterLocationInput
          key={`co-loc-${loc.display_name ?? loc.region ?? loc.country}-${i}`}
          location={loc}
          onChange={(updated) => {
            const next = [...(filters.company_locations ?? [])];
            next[i] = updated;
            updateFilter("company_locations", next);
          }}
          onRemove={() =>
            updateFilter(
              "company_locations",
              (filters.company_locations ?? []).filter((_, j) => j !== i),
            )
          }
        />
      ))}

      <FilterFieldContainer>
        <FilterFieldLabel>Tags</FilterFieldLabel>
        <FilterFieldContent>
          <AutocompleteInput
            variant="filter"
            field="company_tags"
            placeholder="Search..."
            showAllOnOpen
            onSelect={(result: AutocompleteResult) => addTag(result.value)}
            onFreeText={(text: string) => addTag(text.toLowerCase())}
          />
        </FilterFieldContent>
      </FilterFieldContainer>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag, i) => {
            const excluded = tag.mode === "exclude";
            const required = tag.required ?? false;
            return (
              <FilterChip
                key={i}
                label={tag.text}
                variants={tag.variants}
                beforeActions={
                  <VariantsDropdown
                    compact
                    field="company_tags"
                    item={tag}
                    onChange={(updated) => {
                      const next = [...tags];
                      next[i] = updated;
                      updateFilter("company_tags", next);
                    }}
                  />
                }
                excluded={excluded}
                required={required}
                onToggleExclude={() => {
                  const next = [...tags];
                  next[i] = { ...tag, mode: excluded ? "include" : "exclude" };
                  updateFilter("company_tags", next);
                }}
                onToggleRequired={() => {
                  const next = [...tags];
                  next[i] = { ...tag, required: !required };
                  updateFilter("company_tags", next);
                }}
                onRemove={() =>
                  updateFilter(
                    "company_tags",
                    tags.filter((_, j) => j !== i),
                  )
                }
              />
            );
          })}
          <ClearChipsButton
            onClick={() => updateFilter("company_tags", undefined)}
          />
        </div>
      )}

      <FilterFieldContainer>
        <FilterFieldLabel>Company Size</FilterFieldLabel>
        <FilterFieldContent>
          <AutocompleteInput
            variant="filter"
            field="company_sizes"
            placeholder="Search..."
            staticItems={COMPANY_SIZES}
            selectedValues={filters.company_sizes ?? undefined}
            allowFreeText={false}
            onSelect={(result: AutocompleteResult) => {
              if (!isCompanySize(result.value)) return;
              const value = result.value;
              const existing = filters.company_sizes ?? [];
              if (existing.includes(value)) {
                const next = existing.filter((s) => s !== value);
                updateFilter(
                  "company_sizes",
                  next.length > 0 ? next : undefined,
                );
              } else {
                updateFilter("company_sizes", [...existing, value]);
              }
            }}
          />
        </FilterFieldContent>
      </FilterFieldContainer>
      {(filters.company_sizes ?? []).length > 0 && (
        <div className="flex flex-wrap gap-1">
          {(filters.company_sizes ?? []).map((size, i) => (
            <div
              key={i}
              className="flex items-center gap-1 border border-border bg-contrast/4 rounded px-1.5 h-[26px]"
            >
              <span className="text-xs font-normal whitespace-nowrap tracking-[-0.12px] text-contrast/80">
                {COMPANY_SIZE_LABELS[size] ?? size}
              </span>
              <button
                type="button"
                onClick={() => {
                  const next = (filters.company_sizes ?? []).filter(
                    (_, j) => j !== i,
                  );
                  updateFilter(
                    "company_sizes",
                    next.length > 0 ? next : undefined,
                  );
                }}
                className="shrink-0 cursor-pointer opacity-40 hover:opacity-70 transition-opacity"
              >
                <Icon
                  name="IconCrossMedium"
                  size={12}
                  className="text-heading"
                />
              </button>
            </div>
          ))}
          <ClearChipsButton
            onClick={() => updateFilter("company_sizes", undefined)}
          />
        </div>
      )}

      <FilterFieldContainer>
        <FilterFieldLabel>Founded After</FilterFieldLabel>
        <FilterFieldContent>
          <FilterNumberInput
            value={filters.company_founded_after ?? undefined}
            onChange={(v) => updateFilter("company_founded_after", v)}
            min={1900}
            max={new Date().getFullYear()}
            placeholder={String(new Date().getFullYear())}
          />
        </FilterFieldContent>
      </FilterFieldContainer>

      <FilterCheckboxInput
        checked={companyExclusions}
        onChange={setCompanyExclusions}
      >
        Remove your{" "}
        <span className="text-primary dark:text-[#91acff] underline decoration-solid">
          Company Exclusions
        </span>{" "}
        from results
      </FilterCheckboxInput>
    </>
  );
}
